/**
 * EAVES IN A PHASE (2026-10-30, Matt: "it's super common for us to change existing eaves ... say
 * what they are set to in existing phase and what we want them changed to in the new
 * construction and let the program resolve the differences and make the demo and improvement
 * plans look right according to the phase and view settings").
 *
 * An existing roof keeps ONE record. Its eave settings as they exist are the record's own values;
 * what they become is a change in a later phase (phaseChanges.ts — instance params, plus per-edge
 * attrs on the boundary). Nothing is scoped by hand: the geometry compares the roof as it stood
 * the phase before with the roof as viewed, edge by edge, and only the eave PARTS that differ on
 * an edge are drawn demolished (old) and new (new) along that edge. An untouched edge stays
 * existing.
 *
 * - `setEaveEditPhase` is the session switch the roof's Properties show ("Eaves as: Existing |
 *   New Construction"). Only eave keys on that one roof route into a change; slope, plate height
 *   and the boundary's shape always write the roof itself.
 * - `eaveEdgeChanges(before, after)` is the comparison: per boundary edge (keyed by its FROM node
 *   id), the parts that differ. Pure.
 */
import type { ParamValue } from './types'
import type { ResolvedElement } from './params'

/** per-edge eave attrs on a roof boundary node (types.ts PathNode.edge) */
export const ROOF_EAVE_EDGE_KEYS = [
  'overhangFt', 'eaveStructureDepthIn', 'tails', 'flatSoffit', 'fascia', 'gutter', 'barge', 'frieze',
] as const

/** a roof's instance eave settings (roof schema: Eaves, Rafter Tails, Fascia & Gutters,
 *  Outlookers & Barge) — the keys an eave change may record */
export const ROOF_EAVE_PARAM_KEYS = [
  'overhangFt', 'eaveMode', 'eaveStructureDepthIn', 'deckingThicknessIn', 'deckingMaterial',
  'soffitMaterial', 'soffitStyle', 'soffitReturnFt', 'soffitFramingSize',
  'tailsDefault', 'tailSize', 'tailSpacingIn', 'tailStyle', 'friezeDefault', 'friezeSize',
  'fasciaDefault', 'fasciaSize', 'fasciaMaterial',
  'gutterDefault', 'gutterProfile', 'gutterSize', 'gutterMaterial', 'downspoutShape', 'downspoutBend', 'downspoutEnd',
  'bargeDefault', 'bargeSize', 'outlookerSize', 'outlookerSpacingIn', 'outlookerLayout', 'outlookerCount', 'outlookerStyle',
  'tailColor', 'fasciaColor', 'soffitColor', 'gutterColor', 'outlookerColor', 'bargeColor',
] as const
const EAVE_PARAM_SET = new Set<string>(ROOF_EAVE_PARAM_KEYS)
const EAVE_EDGE_SET = new Set<string>(ROOF_EAVE_EDGE_KEYS)
export const isRoofEaveParam = (k: string): boolean => EAVE_PARAM_SET.has(k)
export const isRoofEaveEdgeKey = (k: string): boolean => EAVE_EDGE_SET.has(k)

// ---------- the session switch ----------
const eaveEdit = new WeakMap<object, { id: string; phase: string }>()
/** the roof (one at a time) whose eave edits are recorded as a change in `phase`; null clears */
export function setEaveEditPhase(doc: object, elementId: string | null, phase: string | null): void {
  if (!elementId || !phase) eaveEdit.delete(doc)
  else eaveEdit.set(doc, { id: elementId, phase })
}
/** the phase an eave edit to this roof is recorded in, or null = write the roof itself */
export function eaveEditPhaseFor(doc: object, elementId: string): string | null {
  const e = eaveEdit.get(doc)
  return e && e.id === elementId ? e.phase : null
}

// ---------- the comparison ----------
export type EavePart = 'structure' | 'roofing' | 'fascia' | 'gutters' | 'soffit' | 'eaveFraming'
const EAVE_ONLY: EavePart[] = ['fascia', 'gutters', 'soffit', 'eaveFraming']
const ALL: EavePart[] = ['structure', 'roofing', ...EAVE_ONLY]

export interface EaveEdgeChange { nodeId: string; parts: EavePart[] }

type Node = { id?: string; edge?: Record<string, unknown> }
const nodesOf = (el: ResolvedElement): Node[] =>
  ((el.params.boundary as { nodes?: Node[] } | null)?.nodes ?? [])
const same = (a: unknown, b: unknown): boolean => JSON.stringify(a ?? null) === JSON.stringify(b ?? null)
const on = (v: unknown): boolean => v === true

/** the eave settings one edge resolves to: the edge's own attr, else the roof's */
function edgeEave(p: Record<string, ParamValue>, e: Record<string, unknown>) {
  return {
    overhang: e.overhangFt ?? p.overhangFt ?? null,
    depth: e.eaveStructureDepthIn ?? p.eaveStructureDepthIn ?? '',
    fascia: on(e.fascia ?? p.fasciaDefault),
    gutter: on(e.gutter ?? p.gutterDefault),
    tails: on(e.tails ?? p.tailsDefault),
    barge: on(e.barge ?? p.bargeDefault),
    frieze: on(e.frieze ?? p.friezeDefault),
    flat: on(e.flatSoffit) || p.soffitStyle === 'flat',
    gable: on(e.gable),
  }
}

const diffKeys = (a: Record<string, ParamValue>, b: Record<string, ParamValue>, keys: string[]) =>
  keys.some((k) => !same(a[k], b[k]))

/**
 * Per edge, the eave parts that differ between the roof as it stood (`before`) and as viewed
 * (`after`). Colors alone are a refinish (nothing torn out) and never count. null = nothing
 * drawn differs. When the boundary itself changed (different nodes), every edge's eave parts
 * count as changed — the comparison can't pair the edges.
 */
export function eaveEdgeChanges(before: ResolvedElement, after: ResolvedElement): EaveEdgeChange[] | null {
  if (before.category !== 'roof' || after.category !== 'roof') return null
  const bp = before.params as Record<string, ParamValue>, ap = after.params as Record<string, ParamValue>
  const bn = nodesOf(before), an = nodesOf(after)
  if (an.length < 3) return null
  const paired = bn.length === an.length && bn.every((n, i) => n.id && n.id === an[i].id)
  const per = new Map<string, Set<EavePart>>()
  const add = (id: string, parts: EavePart[]) => {
    let s = per.get(id)
    if (!s) { s = new Set(); per.set(id, s) }
    for (const p of parts) s.add(p)
  }
  const ids = an.map((n, i) => n.id ?? `#${i}`)
  if (!paired || !same(bp.cricket, ap.cricket)) {
    for (const id of ids) add(id, ALL)
  } else {
    // roof-wide settings that change a part wherever that part exists (either side)
    const fasciaKeys = diffKeys(bp, ap, ['fasciaSize', 'fasciaMaterial', 'friezeSize'])
    const bargeKeys = diffKeys(bp, ap, ['bargeSize', 'outlookerSize', 'outlookerSpacingIn', 'outlookerLayout', 'outlookerCount', 'outlookerStyle'])
    const gutterKeys = diffKeys(bp, ap, ['gutterProfile', 'gutterSize', 'gutterMaterial', 'downspoutShape', 'downspoutBend', 'downspoutEnd'])
    const tailKeys = diffKeys(bp, ap, ['tailSize', 'tailSpacingIn', 'tailStyle'])
    const soffitKeys = diffKeys(bp, ap, ['eaveMode', 'soffitMaterial', 'soffitReturnFt', 'soffitFramingSize', 'deckingThicknessIn', 'deckingMaterial'])
    an.forEach((n, i) => {
      const id = ids[i]
      const b = edgeEave(bp, bn[i].edge ?? {}), a = edgeEave(ap, n.edge ?? {})
      if (!same(b.overhang, a.overhang) || b.gable !== a.gable) { add(id, ALL); return }
      const parts: EavePart[] = []
      const anyFascia = b.fascia || a.fascia || b.frieze || a.frieze
      if (b.fascia !== a.fascia || b.frieze !== a.frieze || (anyFascia && fasciaKeys)) parts.push('fascia')
      if (b.barge !== a.barge || ((b.barge || a.barge) && bargeKeys)) parts.push('fascia', 'eaveFraming')
      if (b.gutter !== a.gutter || ((b.gutter || a.gutter) && gutterKeys)) parts.push('gutters')
      if (b.tails !== a.tails || ((b.tails || a.tails) && tailKeys) || !same(b.depth, a.depth)) parts.push('eaveFraming')
      if (b.flat !== a.flat || soffitKeys) parts.push('soffit', 'eaveFraming')
      if (parts.length) add(id, parts)
    })
    // a gutter turns the corner onto the next edge's line: a changed gutter takes its gable
    // neighbors with it so the corner piece isn't left half old, half new
    ids.forEach((id, i) => {
      if (!per.get(id)?.has('gutters')) return
      for (const j of [(i + ids.length - 1) % ids.length, (i + 1) % ids.length]) {
        if (on(an[j].edge?.gable)) add(ids[j], ['gutters'])
      }
    })
  }
  if (!per.size) return null
  return ids.filter((id) => per.has(id)).map((id) => ({ nodeId: id, parts: ALL.filter((p) => per.get(id)!.has(p)) }))
}

/**
 * ROOF KEYNOTES (2026-10-30): which eave parts each boundary edge of a roof actually carries
 * (keyed by the edge's FROM node id) — so a keynote says "remove" only where a part was, and
 * "new" only where one goes. The body and roofing run on every edge; the overhang is real only
 * where it is above zero.
 */
export function eavePartsPresent(el: ResolvedElement): Map<string, Set<EavePart>> {
  const out = new Map<string, Set<EavePart>>()
  if (el.category !== 'roof') return out
  const p = el.params as Record<string, ParamValue>
  const cricket = p.cricket === true
  nodesOf(el).forEach((n, i) => {
    const e = edgeEave(p, n.edge ?? {})
    const s = new Set<EavePart>(['structure', 'roofing'])
    if (!cricket) {
      if (e.fascia || e.frieze || e.barge) s.add('fascia')
      if (e.gutter && !e.gable) s.add('gutters')
      if (p.eaveMode !== 'open' || e.flat) s.add('soffit')
      if (e.tails || e.barge) s.add('eaveFraming')
    }
    out.set(n.id ?? `#${i}`, s)
  })
  return out
}
